/**
 * Video Hero Banner Component
 * Full-width responsive video slideshow with touch swipe and smart autoplay
 */

class VideoHeroBanner extends HTMLElement {
  constructor() {
    super();
    this.track = null;
    this.slides = [];
    this.dots = [];
    this.prevBtn = null;
    this.nextBtn = null;
    this.currentIndex = 0;
    this.timer = null;
    this.isTransitioning = false;
    this.touchStartX = 0;
    this.touchEndX = 0;
  }

  connectedCallback() {
    this.track = this.querySelector('[data-hero-track]');
    this.slides = Array.from(this.querySelectorAll('.video-hero-slide'));
    if (!this.slides.length) return;

    this.prevBtn = this.querySelector('[data-hero-prev]');
    this.nextBtn = this.querySelector('[data-hero-next]');
    this.dots = Array.from(this.querySelectorAll('.video-hero-dot'));

    this.transitionStyle = this.dataset.transition || 'slide';
    this.autoRotate = this.dataset.autoRotate === 'true';
    this.speed = parseInt(this.dataset.speed, 10) * 1000 || 5000;
    this.pauseOnHover = this.dataset.pauseOnHover === 'true';

    this.initSlides();
    this.initControls();
    this.initTouch();
    this.initAudioToggles();

    if (this.autoRotate && this.slides.length > 1) {
      this.startTimer();
      if (this.pauseOnHover) {
        this.addEventListener('mouseenter', () => this.stopTimer());
        this.addEventListener('mouseleave', () => this.startTimer());
      }
    }

    // Shopify Theme Editor support
    if (window.Shopify && window.Shopify.designMode) {
      document.addEventListener('shopify:block:select', (evt) => {
        if (evt.target && evt.target.closest('video-hero-banner') === this) {
          const slide = evt.target.closest('.video-hero-slide');
          if (slide) {
            const index = this.slides.indexOf(slide);
            if (index !== -1) this.goToSlide(index);
          }
        }
      });
    }

    // Play initial video
    this.updateActiveMedia();

    this.resizeHandler = () => this.updateActiveMedia();
    window.addEventListener('resize', this.resizeHandler, { passive: true });
  }

  disconnectedCallback() {
    this.stopTimer();
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
    }
  }

  initSlides() {
    this.slides.forEach((slide, idx) => {
      if (idx === 0) {
        slide.classList.add('is-active');
      } else {
        slide.classList.remove('is-active');
      }
    });
    this.updateDots();
  }

  initControls() {
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.prevSlide();
      });
    }

    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.nextSlide();
      });
    }

    this.dots.forEach((dot, idx) => {
      dot.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.goToSlide(idx);
      });
    });
  }

  initTouch() {
    let startX = 0;
    let endX = 0;

    this.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
      this.stopTimer();
    }, { passive: true });

    this.addEventListener('touchend', (e) => {
      endX = e.changedTouches[0].clientX;
      const diff = startX - endX;
      if (Math.abs(diff) > 40) {
        if (diff > 0) {
          this.nextSlide();
        } else {
          this.prevSlide();
        }
      }
      if (this.autoRotate) this.startTimer();
    }, { passive: true });
  }

  initAudioToggles() {
    const audioBtns = this.querySelectorAll('[data-hero-audio]');
    audioBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        const activeSlide = this.slides[this.currentIndex];
        if (!activeSlide) return;

        const videos = activeSlide.querySelectorAll('video');
        if (!videos.length) return;

        let shouldMute = !videos[0].muted;
        videos.forEach(v => { v.muted = shouldMute; });

        const iconMuted = btn.querySelector('.icon-muted');
        const iconUnmuted = btn.querySelector('.icon-unmuted');
        if (iconMuted && iconUnmuted) {
          iconMuted.style.display = shouldMute ? 'block' : 'none';
          iconUnmuted.style.display = shouldMute ? 'none' : 'block';
        }
      });
    });
  }

  goToSlide(index) {
    if (index === this.currentIndex || this.isTransitioning) return;
    this.isTransitioning = true;

    // Wrap index
    if (index >= this.slides.length) {
      this.currentIndex = 0;
    } else if (index < 0) {
      this.currentIndex = this.slides.length - 1;
    } else {
      this.currentIndex = index;
    }

    if (this.transitionStyle === 'fade') {
      this.slides.forEach((slide, i) => {
        slide.classList.toggle('is-active', i === this.currentIndex);
      });
    } else {
      if (this.track) {
        this.track.style.transform = `translateX(-${this.currentIndex * 100}%)`;
      }
      this.slides.forEach((slide, i) => {
        slide.classList.toggle('is-active', i === this.currentIndex);
      });
    }

    this.updateDots();
    this.updateActiveMedia();

    setTimeout(() => {
      this.isTransitioning = false;
    }, 650);
  }

  nextSlide() {
    this.goToSlide(this.currentIndex + 1);
  }

  prevSlide() {
    this.goToSlide(this.currentIndex - 1);
  }

  updateDots() {
    if (!this.dots.length) return;
    this.dots.forEach((dot, idx) => {
      dot.classList.toggle('is-active', idx === this.currentIndex);
    });
  }

  updateActiveMedia() {
    this.slides.forEach((slide, idx) => {
      const videos = slide.querySelectorAll('video');
      if (idx === this.currentIndex) {
        videos.forEach(v => {
          // If video is meant for mobile only, do not play on desktop; if for desktop only, do not play on mobile
          const isMobileMedia = v.classList.contains('video-hero-media--mobile');
          const isDesktopMedia = v.classList.contains('video-hero-media--desktop');
          const isMobileScreen = window.innerWidth <= 749;

          if ((isMobileMedia && !isMobileScreen) || (isDesktopMedia && isMobileScreen)) {
            v.pause();
            return;
          }

          v.muted = true;
          v.defaultMuted = true;
          v.volume = 0;
          v.setAttribute('muted', '');
          v.setAttribute('playsinline', '');
          v.setAttribute('webkit-playsinline', 'true');
          v.setAttribute('autoplay', '');
          
          const attemptPlay = () => {
            if (!v.paused && v.currentTime > 0) return;
            const playPromise = v.play();
            if (playPromise !== undefined) {
              playPromise.catch(() => {
                // If restricted by browser (e.g., iOS Low Power Mode), unlock on first user scroll/touch
                const unlockPlay = () => {
                  v.play().catch(() => {});
                };
                window.addEventListener('touchstart', unlockPlay, { once: true, passive: true });
                window.addEventListener('touchend', unlockPlay, { once: true, passive: true });
                window.addEventListener('scroll', unlockPlay, { once: true, passive: true });
                window.addEventListener('click', unlockPlay, { once: true, passive: true });
              });
            }
          };

          attemptPlay();

          if (v.readyState < 2) {
            v.addEventListener('loadedmetadata', attemptPlay, { once: true });
            v.addEventListener('canplay', attemptPlay, { once: true });
            v.addEventListener('loadeddata', attemptPlay, { once: true });
          }
        });
      } else {
        videos.forEach(v => {
          v.pause();
        });
      }
    });
  }

  startTimer() {
    this.stopTimer();
    this.timer = setInterval(() => {
      this.nextSlide();
    }, this.speed);
  }

  stopTimer() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }
}

if (!customElements.get('video-hero-banner')) {
  customElements.define('video-hero-banner', VideoHeroBanner);
}
